import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { EmailLog, EmailLogFilter } from "../../types/logging";
import {
  getEmailLogsPaginated,
} from "../../services/loggingService";
import { format } from "date-fns";
import { QueryDocumentSnapshot } from "firebase/firestore";

const MailerLogs: React.FC = () => {
  const theme = useTheme();
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState<EmailLogFilter>({
    emailType: "all",
    status: "all",
    search: "",
  });
  const PAGE_SIZE = 100;

  const columns: GridColDef[] = [
    {
      field: "timestamp",
      headerName: "Date",
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        return format(params.value.toDate(), "MMM dd, yyyy HH:mm");
      },
    },
    {
      field: "recipientEmail",
      headerName: "Recipient",
      width: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography variant="body2">{params.value}</Typography>
          {params.row.recipientName && (
            <Typography variant="caption" color="text.secondary">
              {params.row.recipientName}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "subject",
      headerName: "Subject",
      width: 300,
    },
    {
      field: "emailType",
      headerName: "Type",
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          variant="outlined"
          size="small"
          color={params.value === "financial" ? "primary" : "secondary"}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === "sent"
              ? "success"
              : params.value === "failed"
              ? "error"
              : "default"
          }
        />
      ),
    },
    {
      field: "testMode",
      headerName: "Test",
      width: 80,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Chip label="Test" size="small" color="warning" />
        ) : null,
    },
    {
      field: "month",
      headerName: "Period",
      width: 120,
      renderCell: (params: GridRenderCellParams) =>
        params.row.month && params.row.year
          ? `${params.row.month} ${params.row.year}`
          : "",
    },
  ];

  useEffect(() => {
    // Reset and fetch first page when filters change
    const fetchInitialLogs = async () => {
      setLoading(true);
      setLogs([]);
      setLastDoc(null);
      try {
        const result = await getEmailLogsPaginated(filters, PAGE_SIZE);
        setLogs(result.logs);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      } catch (error) {
        console.error("Error fetching logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialLogs();
  }, [filters]);

  const handleFilterChange = (field: keyof EmailLogFilter, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRefresh = async () => {
    setLoading(true);
    setLogs([]);
    setLastDoc(null);
    try {
      const result = await getEmailLogsPaginated(filters, PAGE_SIZE);
      setLogs(result.logs);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error("Error refreshing logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!hasMore || !lastDoc || loadingMore) return;
    
    setLoadingMore(true);
    try {
      const result = await getEmailLogsPaginated(filters, PAGE_SIZE, lastDoc);
      setLogs((prev) => [...prev, ...result.logs]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error("Error loading more logs:", error);
    } finally {
      setLoadingMore(false);
    }
  };
  
  const handleExport = () => {
    const csvContent = [
      "Date,Recipient,Subject,Type,Status,Test Mode,Period",
      ...logs.map(
        (log) =>
          `${format(log.timestamp.toDate(), "yyyy-MM-dd HH:mm")},${
            log.recipientEmail
          },"${log.subject}",${log.emailType},${log.status},${
            log.testMode ? "Yes" : "No"
          },${log.month && log.year ? `${log.month} ${log.year}` : ""}`
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `email-logs-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ height: "100%", width: "100%", p: { xs: 2, sm: 2, md: 2 }, pl: { xs: 2, sm: 3, md: 3 }, pr: { xs: 2, sm: 3, md: 4 } }}>
      <Box
        sx={{
          p: 0,
          mb: 4,
          ml: -1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: "bold" }}>Email Logs</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={logs.length === 0}
            size="small"
          >
            Export
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 0, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={filters.search || ""}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            placeholder="Search by email, name, subject..."
            helperText="Searches across all logs (not just loaded records)"
            sx={{ minWidth: 250, height: 40 }}
          />

          <FormControl size="small" sx={{ minWidth: 120, height: 40 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.emailType || "all"}
              label="Type"
              onChange={(e) => handleFilterChange("emailType", e.target.value)}
              sx={{ height: 40 }}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="financial">Financial</MenuItem>
              <MenuItem value="newsletter">Newsletter</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120, height: 40 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || "all"}
              label="Status"
              onChange={(e) => handleFilterChange("status", e.target.value)}
              sx={{ height: 40 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>

          {/* Date pickers temporarily disabled due to build issues */}
          {/* 
          <DatePicker
            label="Start Date"
            value={filters.startDate || null}
            onChange={(date: Date | null) => handleFilterChange("startDate", date)}
            slotProps={{ textField: { size: "small" } }}
          />

          <DatePicker
            label="End Date"
            value={filters.endDate || null}
            onChange={(date: Date | null) => handleFilterChange("endDate", date)}
            slotProps={{ textField: { size: "small" } }}
          />
          */}

          {hasMore && logs.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleLoadMore}
              disabled={loadingMore || loading}
              sx={{ height: 40 }}
            >
              {loadingMore ? "Loading..." : `Load More (${logs.length} loaded)`}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ height: 600, width: "100%", bgcolor: "transparent", ml: -1 }}>
        <DataGrid
          rows={logs}
          columns={columns}
          loading={loading}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          getRowId={(row) => row.id || row.timestamp.seconds}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.grey[50],
              borderBottom: `2px solid ${theme.palette.divider}`,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default MailerLogs;
